import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { ADMIN_DELETE_USER } from '../recovery/graphql';

// Typed locally so this compiles before codegen regenerates __gqltypes__ from
// the updated server schema.
type AdminDeleteUserResult = {
  adminDeleteUser: { success: boolean; message: string | null };
};
type AdminDeleteUserVars = { input: { userId: string } };

// Minimal, source-agnostic props so this modal works both from the Account
// Recovery search results (AdminUserSummary) and the devtools User View
// (SearchUserQuery.user), which expose different field names.
type Props = {
  userId: string;
  displayName: string;
  email?: string | null;
  firebaseEmail?: string | null;
  providers?: string[];
  onHide: () => void;
  onSuccess?: () => void;
};

// Deleting an account is irreversible — there is no tool to restore it. Three
// distinct gates guard it: 1) type the exact userId + acknowledge, 2) a "last
// chance" screen, 3) type the word DELETE, then fire.
const DELETE_KEYWORD = 'DELETE';

export default function DeleteUserModal({
  userId,
  displayName,
  email,
  firebaseEmail,
  providers,
  onHide,
  onSuccess,
}: Props): JSX.Element {
  const [step, setStep] = useState<'form' | 'confirm' | 'final'>('form');
  const [confirmText, setConfirmText] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [result, setResult] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const [deleteUser, { loading }] = useMutation<AdminDeleteUserResult, AdminDeleteUserVars>(ADMIN_DELETE_USER);

  // Gate 1: type the exact userId to arm the flow — guards against the wrong account.
  const userIdMatches = confirmText.trim() === userId;
  const canContinue = userIdMatches && acknowledged && !loading && !result;
  // Gate 3: type the DELETE keyword to arm the final button.
  const finalMatches = finalText.trim().toUpperCase() === DELETE_KEYWORD;
  const canDelete = finalMatches && !loading && !result;

  const handleContinue = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;
    setStep('confirm');
  };

  const handleConfirmDelete = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canDelete) return;
    try {
      const { data } = await deleteUser({ variables: { input: { userId } } });
      if (data?.adminDeleteUser.success) {
        setResult({
          variant: 'success',
          text: data.adminDeleteUser.message ?? 'Account deleted.',
        });
        setTimeout(() => {
          onSuccess?.();
          onHide();
        }, 1500);
      } else {
        setResult({
          variant: 'danger',
          text: data?.adminDeleteUser.message ?? 'Unknown error.',
        });
      }
    } catch (err) {
      setResult({ variant: 'danger', text: (err as Error).message });
    }
  };

  const title =
    step === 'form'
      ? 'Delete account'
      : step === 'confirm'
        ? 'Delete account — are you sure?'
        : 'Delete account — final confirmation';

  const identityBlock = (
    <div className="mb-3 small">
      <div>
        <strong>User:</strong> {displayName} (<code>{userId}</code>)
      </div>
      <div>
        <strong>Email:</strong> {email ?? <em className="text-muted">(none)</em>}
      </div>
      {firebaseEmail !== undefined && (
        <div>
          <strong>Firebase:</strong> {firebaseEmail ?? <em className="text-muted">(none)</em>}
        </div>
      )}
      {providers !== undefined && (
        <div>
          <strong>Providers:</strong>{' '}
          {providers.length > 0 ? providers.join(', ') : <em className="text-muted">(none)</em>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'form' && (
          <>
            <Alert variant="danger" className="py-2">
              <strong>This is permanent and cannot be undone.</strong> It runs the same delete as the in-app
              &quot;delete my account&quot;
            </Alert>

            {identityBlock}

            <Form onSubmit={handleContinue}>
              <Form.Group>
                <Form.Label>
                  Type the user ID to confirm: <code>{userId}</code>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={userId}
                  isInvalid={confirmText.length > 0 && !userIdMatches}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">User ID does not match.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="delete-ack"
                  label="I understand this permanently deletes the account and its progress."
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              </Form.Group>

              <div className="d-flex justify-content-end mt-3">
                <Button variant="secondary" onClick={onHide} className="mr-2">
                  Cancel
                </Button>
                <Button type="submit" variant="danger" disabled={!canContinue}>
                  Continue
                </Button>
              </div>
            </Form>
          </>
        )}

        {step === 'confirm' && (
          <>
            <Alert variant="danger">
              <strong>Are you sure?</strong> You are about to permanently delete this account. This{' '}
              <strong>cannot be undone</strong> and there is no way to recover it afterwards.
            </Alert>

            {identityBlock}

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setStep('form')} className="mr-2">
                Go back
              </Button>
              <Button variant="danger" onClick={() => setStep('final')}>
                Continue
              </Button>
            </div>
          </>
        )}

        {step === 'final' && (
          <>
            <Alert variant="danger">
              <strong>Last step.</strong> Type <code>{DELETE_KEYWORD}</code> below to permanently delete this account.
            </Alert>

            {identityBlock}

            <Form onSubmit={handleConfirmDelete}>
              <Form.Group>
                <Form.Label>
                  Type <code>{DELETE_KEYWORD}</code> to confirm
                </Form.Label>
                <Form.Control
                  type="text"
                  value={finalText}
                  onChange={(e) => setFinalText(e.target.value)}
                  placeholder={DELETE_KEYWORD}
                  disabled={loading || !!result}
                  isInvalid={finalText.length > 0 && !finalMatches}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">Type {DELETE_KEYWORD} exactly.</Form.Control.Feedback>
              </Form.Group>

              {result && (
                <Alert variant={result.variant} className="mt-3">
                  {result.text}
                </Alert>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="secondary"
                  onClick={() => setStep('confirm')}
                  disabled={loading || !!result}
                  className="mr-2"
                >
                  Go back
                </Button>
                <Button type="submit" variant="danger" disabled={!canDelete}>
                  {loading ? 'Deleting...' : 'Permanently delete'}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </>
  );
}
